export const isAuthorizedUser = (userRoles: any[], rolesToCheck: any[]) => {
  return userRoles.some((userRole: string) =>
    rolesToCheck.some((regex: RegExp) => regex.test(userRole)),
  );
};

export const removeDuplicates = (arr: any[]) => {
  const seen = new Set();
  return arr.filter((item) => {
    let key: string;
    try {
      key = JSON.stringify(item); // convert object to string for comparison
    } catch {
      key = String(item);
    }
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const getParamValue = (query = "") => {
  let name = "";
  let email = "";
  if (query?.includes("@") || query?.includes(".")) {
    email = query?.trim();
  } else {
    name = query?.trim();
  }

  let param = "";
  let value = "";
  if (email) {
    param = "email";
    value = email;
  } else {
    param = "name";
    value = name;
  }
  return { param, value };
};

export const downloadJsonFile = (data: any, filename: string) => {
  const jsonData = JSON.stringify(data, null, 2); // Stringify the data with indentation for readability
  const blob = new Blob([jsonData], {
    type: "application/json",
  });
  const fileUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadBlobFile = (data: any, filename: string) => {
  const blob = new Blob([data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const fileUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = fileUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function formatChangeText(change: any): string {
  const parts = change.path.split(" → ");
  const field = parts[parts.length - 1];

  if (change.type === "added") {
    return `Added ${JSON.stringify(change.newValue, null, 2)} to ${field}`;
  }

  if (change.type === "removed") {
    return `Removed ${JSON.stringify(change.oldValue, null, 2)} from ${field}`;
  }

  return `Changed ${field} from ${JSON.stringify(change.oldValue, null, 2)} to ${JSON.stringify(change.newValue, null, 2)}`;
}

type Change = {
  path: string;
  type: "added" | "removed" | "changed";
  oldValue?: any;
  newValue?: any;
};

export function compareJson(
  oldData: any,
  newData: any,
  path: string[] = [],
  changes: Change[] = [],
): Change[] {
  // Added
  if (oldData === undefined) {
    changes.push({
      path: path.join(" → "),
      type: "added",
      newValue: newData,
    });
    return changes;
  }

  // Removed
  if (newData === undefined) {
    changes.push({
      path: path.join(" → "),
      type: "removed",
      oldValue: oldData,
    });
    return changes;
  }

  // Primitive changed
  if (
    typeof oldData !== "object" ||
    typeof newData !== "object" ||
    oldData === null ||
    newData === null
  ) {
    if (oldData !== newData) {
      changes.push({
        path: path.join(" → "),
        type: "changed",
        oldValue: oldData,
        newValue: newData,
      });
    }
    return changes;
  }

  // Arrays
  if (Array.isArray(oldData) && Array.isArray(newData)) {
    const max = Math.max(oldData.length, newData.length);
    for (let i = 0; i < max; i++) {
      compareJson(oldData[i], newData[i], [...path, String(i)], changes);
    }
    return changes;
  }

  // Objects
  const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)]);

  keys.forEach((key) => {
    compareJson(oldData[key], newData[key], [...path, key], changes);
  });

  return changes;
}

type UiChange = {
  type: "added" | "removed" | "changed";
  text: string | Change;
};

type Section = {
  title: string;
  changeCount: number;
  changes: UiChange[];
};

export function buildSections(changes: any[]): Section[] {
  const map: Record<string, Section> = {};

  changes.forEach((change) => {
    const parts = change.path.split(" → ");
    const title = parts.slice(0, -1).join(" → ") || "Root";

    if (!map[title]) {
      map[title] = {
        title,
        changeCount: 0,
        changes: [],
      };
    }

    map[title].changes.push({
      type: change.type,
      text: formatChangeText(change),
    });
  });

  return Object.values(map)?.map((section) => ({
    ...section,
    changeCount: section.changes.length,
  }));
}

type SectionTree = {
  title: string;
  changeCount: number;
  changes: UiChange[];
  children: Record<string, SectionTree>;
};

function createSection(title: string): SectionTree {
  return {
    title,
    changeCount: 0,
    changes: [],
    children: {},
  };
}

export function buildSectionsTree(changes: Change[]): SectionTree[] {
  const root = createSection("Root");

  changes.forEach((change) => {
    const parts = change.path.split(" → ");
    let current = root;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];

      if (!current.children[part]) {
        current.children[part] = createSection(part);
      }

      current = current.children[part];
    }

    current.changes.push({
      type: change.type,
      text: change,
    });
  });

  function computeCounts(section: SectionTree): number {
    let count = section.changes.length;

    Object.values(section.children).forEach((child) => {
      count += computeCounts(child);
    });

    section.changeCount = count;
    return count;
  }

  computeCounts(root);

  return Object.values(root.children);
}

export function processChanges(node: any) {
  // If the current node has no changes and has exactly one child
  if (node.changes?.length === 0 && Object.keys(node.children).length === 1) {
    const firstChildKey = Object.keys(node.children)[0];
    const firstChild = node.children[firstChildKey];

    // Concatenate the current title with the title of its first child
    node.title = `${node.title} / ${firstChild.title}`;

    // Recursively process the first child's children (preserve the structure of changes deeper)
    node.children = firstChild.children;

    // If the child has changes, we should also propagate them upwards
    node.changes = [...(firstChild.changes || [])];
    processChanges(node);
  }

  // Recursively process each child (if any)
  Object.values(node.children).forEach((child: any) => {
    processChanges(child);
  });

  return node;
}

export const decodePolicyData = (rawData: any, base64Key: any): any => {
  const tryParseJson = (value: string): any | null => {
    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  };

  const decodeBase64 = (value: string): string | null => {
    try {
      const normalized = value.replace(/\s/g, "");

      // Browser path
      if (typeof atob === "function") {
        const binary = atob(normalized);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

        if (typeof TextDecoder !== "undefined") {
          return new TextDecoder().decode(bytes);
        }

        return decodeURIComponent(
          Array.from(bytes)
            .map((byte) => `%${byte.toString(16).padStart(2, "0")}`)
            .join(""),
        );
      }

      // Node-like path
      const bufferCtor = (globalThis as any).Buffer;
      if (bufferCtor) {
        return bufferCtor.from(normalized, "base64").toString("utf-8");
      }

      return null;
    } catch (_error) {
      return null;
    }
  };

  const decryptWithBase64Key = (
    encryptedValue: string,
    base64Key: string,
  ): string | null => {
    try {
      const key = Buffer.from(base64Key, "base64");
      if (key.length !== 32) {
        return null;
      }

      const [ivPart, payloadPart] = encryptedValue.split(":");
      if (!ivPart || !payloadPart) {
        return null;
      }

      const iv = Buffer.from(ivPart, "base64");
      const payload = Buffer.from(payloadPart, "base64");

      if (iv.length === 12 && payload.length > 16) {
        const cryptoModule = require("crypto");
        const authTag = payload.subarray(payload.length - 16);
        const ciphertext = payload.subarray(0, payload.length - 16);
        const decipher = cryptoModule.createDecipheriv("aes-256-gcm", key, iv);
        decipher.setAuthTag(authTag);
        const decrypted = Buffer.concat([
          decipher.update(ciphertext),
          decipher.final(),
        ]);
        return decrypted.toString("utf-8");
      }

      if (iv.length === 16) {
        const cryptoModule = require("crypto");
        const decipher = cryptoModule.createDecipheriv("aes-256-cbc", key, iv);
        const decrypted = Buffer.concat([
          decipher.update(payload),
          decipher.final(),
        ]);
        return decrypted.toString("utf-8");
      }

      return null;
    } catch (_error) {
      return null;
    }
  };

  let payload = rawData as any;

  // Unwrap common API gateway wrappers until the inner payload is reached.
  while (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Object.prototype.hasOwnProperty.call(payload, "data")
  ) {
    payload = payload.data;
  }

  if (!payload) {
    return payload;
  }

  if (typeof payload === "object") {
    return payload;
  }

  if (typeof payload !== "string") {
    return payload;
  }

  const parsedRaw = tryParseJson(payload);
  if (parsedRaw) {
    return parsedRaw;
  }

  const decodedRaw = decodeBase64(payload);
  if (decodedRaw) {
    const parsedBase64 = tryParseJson(decodedRaw);
    if (parsedBase64) {
      return parsedBase64;
    }
  }

  if (base64Key) {
    const decrypted = decryptWithBase64Key(payload, base64Key);
    if (decrypted) {
      const parsedDecrypted = tryParseJson(decrypted);
      if (parsedDecrypted) {
        return parsedDecrypted;
      }

      const decodedDecrypted = decodeBase64(decrypted);
      if (decodedDecrypted) {
        const parsedDecodedDecrypted = tryParseJson(decodedDecrypted);
        if (parsedDecodedDecrypted) {
          return parsedDecodedDecrypted;
        }
      }
    }
  }

  return payload;
};
